import { useState } from "react";
import { useTranslation } from "react-i18next";

const SHORT_DOMAIN = "tales.wtf";

// Only platforms with a real, documented share intent get a button.
// Discord and Instagram have none, and the "copy the link, then open the
// platform's front page" workaround that used to stand in for one just
// dropped the visitor on discord.com/app or instagram.com with no
// indication of what had happened — the plain copy-link button at the end
// of this bar does the same job honestly (§ T-04).
const PLATFORMS = [
  {
    key: "whatsapp",
    label: "WhatsApp",
    href: (url, title) => `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="12" cy="11" r="8" />
        <polygon points="7,17 5,21 9,19" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    key: "telegram",
    label: "Telegram",
    href: (url, title) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <polygon points="3,12 21,4 15,20 11,13 3,12" />
      </svg>
    ),
  },
  {
    key: "facebook",
    label: "Facebook",
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20">
        <circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
        <text x="12" y="16.5" textAnchor="middle" fontSize="12" fontFamily="Georgia, serif" fill="currentColor">
          f
        </text>
      </svg>
    ),
  },
  {
    key: "threads",
    label: "Threads",
    href: (url, title) => `https://www.threads.net/intent/post?text=${encodeURIComponent(`${title} ${url}`)}`,
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="9.5" cy="12" r="6" />
        <circle cx="14.5" cy="12" r="6" />
      </svg>
    ),
  },
];

const iconButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "38px",
  height: "38px",
  borderRadius: "999px",
  background: "rgba(254,250,239,0.08)",
  border: "none",
  color: "var(--color-cream)",
  cursor: "pointer",
};

/** Share bar for an event/project page: one button per platform that has
 * a real share intent, plus a copy-link button for everywhere else.
 * Shares the tales.wtf short URL rather than the long canonical one —
 * scripts/server.cjs 301s its /go/:id target to the right language's
 * page, so a shared link resolves for a scraper too, not just once the
 * SPA has booted. */
export default function ShareLinks({ shortUrl, title, showLabel = true }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!shortUrl) return null;

  const url = `https://${SHORT_DOMAIN}/${shortUrl}`;

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      return false; // clipboard API unavailable (older browser, insecure context)
    }
  }

  async function handleCopyClick() {
    if (await copyToClipboard()) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {showLabel && <span style={{ fontSize: "13px", color: "var(--color-cream-faint)" }}>{t("shareLinks.label")}</span>}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
        {PLATFORMS.map((platform) => (
          <a
            key={platform.key}
            href={platform.href(url, title)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("shareLinks.shareVia", { platform: platform.label })}
            title={platform.label}
            style={iconButtonStyle}
          >
            {platform.icon}
          </a>
        ))}
        <button
          type="button"
          onClick={handleCopyClick}
          aria-label={t(copied ? "shareLinks.copied" : "shareLinks.copyLink")}
          title={t(copied ? "shareLinks.copied" : "shareLinks.copyLink")}
          style={{ ...iconButtonStyle, color: copied ? "var(--color-gold)" : "var(--color-cream)" }}
        >
          {copied ? (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="4,13 9,18 20,6" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6">
              <rect x="9" y="9" width="11" height="11" rx="2" />
              <rect x="4" y="4" width="11" height="11" rx="2" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
